import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { ChangeRequestView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  changeRequestBody,
  changeRequestFieldLabelKeys,
  changeRequestFieldNames,
  changeRequestStatusLabelKeys,
  emptyChangeRequestForm,
  type ChangeRequestForm,
} from './changeRequests';

export interface ChangeRequestSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  athleteId: string;
}

type ErrorKey = ApiRequestError['messageKey'];

/** For a guardian: propose a contact change or a correction, and follow what happened to it. */
export function ChangeRequestSection({
  locale,
  base,
  csrfToken,
  athleteId,
}: ChangeRequestSectionProps): React.JSX.Element {
  const path = `${base}/athletes/${athleteId}/change-requests`;
  const [requests, setRequests] = useState<ChangeRequestView[] | null>(null);
  const [form, setForm] = useState<ChangeRequestForm>(emptyChangeRequestForm);
  const [open, setOpen] = useState(false);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => {
    try {
      setRequests(await apiRequest<ChangeRequestView[]>(path));
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  const body = changeRequestBody(form);

  async function submit(): Promise<void> {
    if (body === null) return;
    setErrorKey(null);
    try {
      await apiRequest(path, { method: 'POST', csrfToken, body });
      setForm(emptyChangeRequestForm);
      setOpen(false);
      setSent(true);
      await load();
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  async function withdraw(request: ChangeRequestView): Promise<void> {
    setErrorKey(null);
    try {
      await apiRequest(`${base}/change-requests/${request.id}/withdraw`, {
        method: 'POST',
        csrfToken,
        ifMatchVersion: request.version,
        body: {},
      });
      await load();
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  function textField(
    field: keyof ChangeRequestForm,
    label: Parameters<typeof translate>[1],
    type = 'text',
  ): React.JSX.Element {
    return (
      <TextField
        size="small"
        type={type}
        label={translate(locale, label)}
        value={form[field]}
        onChange={(event) => {
          setForm((current) => ({ ...current, [field]: event.target.value }));
        }}
        slotProps={type === 'date' ? { inputLabel: { shrink: true } } : undefined}
      />
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography component="h4" variant="subtitle2">
        {translate(locale, 'changeRequests.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ my: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {sent && (
        <Alert severity="success" sx={{ my: 1 }}>
          {translate(locale, 'changeRequests.sent')}
        </Alert>
      )}
      {requests?.map((request) => (
        <Stack key={request.id} direction="row" spacing={2} sx={{ alignItems: 'center', py: 0.5 }}>
          <Typography sx={{ flex: 1 }} variant="body2">
            {changeRequestFieldNames(request.proposed)
              .map((field) => translate(locale, changeRequestFieldLabelKeys[field]))
              .join(', ')}{' '}
            — {translate(locale, changeRequestStatusLabelKeys[request.status])}
            {request.decisionReason === null ? '' : ` (${request.decisionReason})`}
          </Typography>
          {request.status === 'pending' && (
            <Button
              size="small"
              onClick={() => {
                void withdraw(request);
              }}
            >
              {translate(locale, 'changeRequests.withdraw')}
            </Button>
          )}
        </Stack>
      ))}
      {!open && (
        <Button
          size="small"
          onClick={() => {
            setOpen(true);
            setSent(false);
          }}
        >
          {translate(locale, 'changeRequests.open')}
        </Button>
      )}
      {open && (
        <Stack spacing={1} sx={{ maxWidth: 420, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'changeRequests.hint')}
          </Typography>
          {textField('email', 'athletes.email')}
          {textField('phone', 'athletes.phone')}
          {textField('givenName', 'athletes.givenName')}
          {textField('familyName', 'athletes.familyName')}
          {textField('dateOfBirth', 'athletes.dateOfBirth', 'date')}
          {textField('note', 'changeRequests.note')}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={body === null}
              onClick={() => {
                void submit();
              }}
            >
              {translate(locale, 'changeRequests.submit')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setOpen(false);
              }}
            >
              {translate(locale, 'changeRequests.cancel')}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
